// Zoid will be bundled in
import * as zoid from  'zoid/dist/zoid.frame';

// React should be set with RayloPayIntegration.init()
// can't `await import(/* webpackIgnore: true */ 'react');` because the caller might have a different bundler instance or even not webpack,
// so 'react' would not be found in the webpack cache and the import above would fail
let React = undefined;

let scriptOrigin = undefined;
if (document?.currentScript) {
  scriptOrigin = new URL(document.currentScript.src).origin;
}

function init(options) {
  if (!!options.scriptOrigin && !scriptOrigin) {
    scriptOrigin = options.scriptOrigin;
  }
  if (!!options.React && !React) {
    React = options.React
  }
  return true;
}

let zoidWidget = undefined;
function getZoidWidgetInstance() {
  if (!zoidWidget) {
    if (!scriptOrigin) {
      throw Error(`Could not determine script origin from environment, set it explicitly using e.g.:
RayloPayIntegration.init({scriptOrigin: "https://widgets.raylopay.com"});`);
    }

    // eslint-disable-next-line no-undef
    zoidWidget = zoid.create({
      tag: "raylo-pay-widget",
      url: scriptOrigin,
      dimensions: {
        width: "100%",
        height: "100%"
      }
    });
  }
  return zoidWidget;
}

function findSingleElement(elementOrselector) {
  if (elementOrselector instanceof Element) {
    return elementOrselector;
  }

  if (typeof elementOrselector !== 'string') {
    throw Error('elementOrselector must be a DOM Element or a string');
  }

  const selector = elementOrselector;
  let element = null;
  if (selector.indexOf('#') === 0) {
    element = document.getElementById(selector.substring(1));
    if (element === null) {
      throw Error(`container with ID ${selector} not found`);
    }
  } else {
    const found = document.querySelectorAll(selector);
    if (found.length === 1) {
        element = found[0];
    } else {
      throw Error(`container selector '${selector}' matched ${found.length} elements, 1 was expected`);
    }
  }

  return element;
}

function assignDefined(target, ...sources) {
  for (const source of sources) {
      for (const key of Object.keys(source)) {
          if (source[key] !== undefined) {
              target[key] = source[key];
          }
      }
  }
  return target;
}

const modalElementId = "rayloPayCheckoutModal";
const modalBackdropElementId = "rayloPayModalBackdrop";
let modalBackdropIsSetup = false;

function setupModalBackdrop() {
  if (modalBackdropIsSetup) {
    return;
  }

  var backdrop = document.createElement("div");
  backdrop.id = modalBackdropElementId;
  backdrop.style.opacity = "0";
  backdrop.style.transition = "opacity .8s linear";
  document.body.prepend(backdrop);
  modalBackdropIsSetup = true;
}

function setModalStyles(element) {
  element.style.display = "block"
  element.style.position = "fixed"
  element.style.top = "0"
  element.style.left = "0"
  element.style.width = "100vw"
  element.style.height = "100vh"
  element.style.opacity = "1"
  element.style.zIndex = 10000
}

var prevBodyOverflowValue;

function showFindOutMoreModal() {
  var modal = document.getElementById(modalElementId);
  if (!modal) {
    modal = document.createElement('div');
    modal.id = modalElementId;
  }
  document.body.prepend(modal); // ensure that the modal is the first element, i.e. in front of the backdrop
  setModalStyles(modal);
  const zoidWidget = getZoidWidgetInstance();
  zoidWidget({
    findOutMoreModal: true,
    handleCloseModal: hideModal
  }).render(`#${modalElementId}`);
  prevBodyOverflowValue = document.body.style.overflow;
  document.body.style.overflow = 'clip';
}

function hideModal() {
  const modalBackdrop = document.getElementById(modalBackdropElementId);
  const modal = document.getElementById(modalElementId);

  document.body.style.overflow = prevBodyOverflowValue;
  modal.style.display = "none";
  if (modalBackdrop) {
    modalBackdrop.style.opacity = "0"
    modalBackdrop.style.display = "none";
  }
}

let generatedStyles = 0;
function addContainerLoadingStyle(container) {
  const styleElement = document.createElement('style');
  styleElement.id = `raylopayWidgetContainerStyle${++generatedStyles}`;
  styleElement.innerText = `#${styleElement.id} + div { height: 100px; margin-bottom: -100px; opacity: 0; }`;
  container.appendChild(styleElement);
  return styleElement;
}
function clearContainerLoadingStyle(styleElement) {
  // in a React context, it may already not be on the page
  styleElement.parentElement?.removeChild(styleElement);
}

const lastRenderedWidget = new Map();

function wrapZoidWidgetFunction({
  widgetType,
  onRender,
  widgetLoadedArgsProcessor
}) {
  return (widgetOptions) => {
    return {
      render: (elementOrselector) => {

        if (!elementOrselector) {
          console.warn(`Attempted to render ${widgetType} to invalid container.`);
          return;
        }

        const doRender = (callerOptions, retriesLeft) => {
          const container = findSingleElement(elementOrselector);
          let containerStyleElement = null;
          let zoidWidget = null;

          function isLatestLoadedInstance() {
            return zoidWidget === lastRenderedWidget.get(elementOrselector);
          }

          const defaultOptions = {
            handleModalOpen: showFindOutMoreModal
          };

          const wrappedOptions = {
            [widgetType]: true,
            onPriceLoaded: (args) => {
              if (isLatestLoadedInstance()) {
                callerOptions.onPriceLoaded?.(args);
              }
            },
            onWidgetLoaded: (args) => {
              if (isLatestLoadedInstance()) {
                if (widgetLoadedArgsProcessor) {
                  args = widgetLoadedArgsProcessor(args);
                }

                callerOptions.onWidgetLoaded?.(args);
                clearContainerLoadingStyle(containerStyleElement);
              }
            },
            onCheckoutUnavailable: () => {
              if (isLatestLoadedInstance()) {
                callerOptions.onCheckoutUnavailable?.();
              }
            },
          };

          const decoratedOptions = assignDefined({}, defaultOptions, callerOptions, wrappedOptions);

          if (lastRenderedWidget.get(elementOrselector)) {
            try {
              lastRenderedWidget.get(elementOrselector).close();
            } catch (err) {
              console.error(err);
            }
          }

          zoidWidget = getZoidWidgetInstance()(decoratedOptions);
          lastRenderedWidget.set(elementOrselector, zoidWidget);

          container.innerHTML = '';
          onRender?.(container);

          containerStyleElement = addContainerLoadingStyle(container);
          zoidWidget.render(container, 'iframe')
            .catch((err) => {
              zoidWidget.close();
              if (err.message?.indexOf('Component closed') !== -1) {
                  // do nothing, why is this even an error...
              } else if (retriesLeft > 0 && err.message && err.message.indexOf('container element removed from DOM') !== -1) {
                  // the page's HTML could have changed in the meantime, so the container can 'disappear'; re-add the widget
                  setTimeout(() => doRender(callerOptions, retriesLeft - 1), 0);
              } else {
                console.error(err);
                wrappedOptions.onCheckoutUnavailable();
              }
            });
        }

        const renderRetries = 3;
        doRender(widgetOptions, renderRetries);
      },
    };
  };
}

function assertGlobalReactIsPresent() {
  if (typeof React === 'undefined') {
    throw Error(`Could not find global React instance; set it explicitly using e.g.:
import React from 'react';
RayloPayIntegration.init({ React });`);
  }
}

let containerComponent = null;
function getContainerComponent() {
  if (containerComponent === null) {
    assertGlobalReactIsPresent();
    containerComponent = React.forwardRef(function ContainerComponent(props, ref) {
      return React.createElement('div', { ...props, ref });
    });
  }
  return containerComponent;
}


function createZoidReactComponent(name, {
  widgetType,
  onRender,
  widgetLoadedArgsProcessor
}) {

  const ZoidReactComponent = function ({
    JWTToken,
    merchantId,
    handleModalOpen,
    checkoutPage,
    checkoutLabel,
    productPage,
    findOutMoreModal,
    handleCloseModal,
    onCheckoutUnavailable,
    onPriceLoaded,
    disableAnalytics,
    onWidgetLoaded,
    ...props
  }) {

    assertGlobalReactIsPresent();

    const [height, setHeight] = React.useState(undefined);

    const widgetLoadedCallback = React.useCallback((e) => {
      setHeight(e.dimensions.height);
      onWidgetLoaded?.(e);
    }, [onWidgetLoaded]);

    const containerRef = React.useRef(null);
    React.useEffect(() => {
      if (containerRef.current != null && JWTToken) {
        const zoidComponent = wrapZoidWidgetFunction({
          widgetType,
          onRender,
          widgetLoadedArgsProcessor
        });
        zoidComponent({
          JWTToken,
          merchantId,
          handleModalOpen,
          checkoutPage,
          checkoutLabel,
          productPage,
          findOutMoreModal,
          handleCloseModal,
          onCheckoutUnavailable,
          onPriceLoaded,
          disableAnalytics,
          onWidgetLoaded: widgetLoadedCallback
        }).render(containerRef.current);
      }
    }, [
      JWTToken,
      merchantId,
      handleModalOpen,
      checkoutPage,
      checkoutLabel,
      productPage,
      findOutMoreModal,
      handleCloseModal,
      onCheckoutUnavailable,
      onPriceLoaded,
      disableAnalytics,
      widgetLoadedCallback
    ]);

    const ContainerComponent = getContainerComponent();

    return React.createElement(ContainerComponent, {
        ...props,
        style: { ...(props.style || {}), height },
        key: 'container',
        ref: containerRef, 
      });
  }

  Object.defineProperty(ZoidReactComponent, "name", { value: name });
  // TODO add an API to update the JWT or other props, cf. https://github.com/krakenjs/zoid/issues/162 -- needs cooperation from the react side of the widgets
  return ZoidReactComponent;
}

export const RayloPayIntegration = Object.freeze({
  init,
  showFindOutMoreModal,
});

const productWidgetOptions = {
  widgetType: 'productPage',
  onRender: setupModalBackdrop
};
export const RayloPayProductWidget = wrapZoidWidgetFunction(productWidgetOptions);
export const RayloPayReactProductWidget = createZoidReactComponent('RayloPayProductWidget', productWidgetOptions)

const checkoutWidgetOptions = {
  widgetType: 'checkoutPage',
  onRender: setupModalBackdrop
};
export const RayloPayCheckoutWidget = wrapZoidWidgetFunction(checkoutWidgetOptions);
export const RayloPayReactCheckoutWidget = createZoidReactComponent('RayloPayCheckoutWidget', checkoutWidgetOptions);

const checkoutLabelWidgetOptions = {
  widgetType: 'checkoutLabel',
  widgetLoadedArgsProcessor: (args) => {
    if (args.dimensions.height < 34) {
      // the recieved dimensions in this callback are likely the ones calculated before the SVG logo is fully loaded, so they are too small / not correct
      args.dimensions.height = 34;
    }
    return args;
  }
};
export const RayloPayCheckoutLabelWidget = wrapZoidWidgetFunction(checkoutLabelWidgetOptions);
export const RayloPayReactCheckoutLabelWidget = createZoidReactComponent('RayloPayCheckoutLabelWidget', checkoutLabelWidgetOptions);

// backwards compatibility and ESM import() support escape hatch
// TODO figure out a better way to export/import the widget react scripts -- probably as an NPM package
// TODO handle better the case of this file being imported multiple times by a loader -- shouldn't happen but happens in React+Webpack when loading dynamically
if (typeof window === 'object' && typeof window.RayloPayIntegration === 'undefined') {
  Object.defineProperties(window, {
    RayloPayWidget: {
      get: () => {
        // TODO remove when all the scripts that depend on these are updated and merchants upgrade
        console.warn("RayloPayWidget is deprecated; use RayloPayProductWidget or RayloPayCheckoutWidget instead.");
        return getZoidWidgetInstance();
      }
    },
    RayloPayProductWidget: { value: RayloPayProductWidget },
    RayloPayReactProductWidget: { value: RayloPayReactProductWidget },
    RayloPayCheckoutWidget: { value: RayloPayCheckoutWidget },
    RayloPayReactCheckoutWidget: { value: RayloPayReactCheckoutWidget },
    RayloPayCheckoutLabelWidget: { value: RayloPayCheckoutLabelWidget },
    RayloPayReactCheckoutLabelWidget: { value: RayloPayReactCheckoutLabelWidget },
    RayloPayIntegration: { value: RayloPayIntegration },
  })
}
